import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Return from "../../../assets/icons/return.svg";
import Camembert from "../../../components/Cac/Partner/Utils/MonitoringElements/Camembert";
import LateRequestsDate from "../../../components/Cac/Partner/Utils/MonitoringElements/LateRequestsDate";
import DeliveryTimes from "../../../components/Cac/Partner/Utils/MonitoringElements/DeliveryTimes";
import RejectedRate from "../../../components/Cac/Partner/Utils/MonitoringElements/RejectedRate";
import AcceptedRate from "../../../components/Cac/Partner/Utils/MonitoringElements/AcceptedRate";
import StatusRequest from "../../../components/Cac/Customer/Request/StatusRequest";
import { IData } from "../../../interfaces/customerPage";
import {
  convertData,
  cutName,
  mathRoundSup,
} from "../../../services/Utils/functionService";
import {
  getDocRequests,
  getLateRequests,
  getMissionAccepted,
  getMissionDataByCustomer,
  getMissionDelivered,
  getMissionRejected,
  getPercentageMission,
  getUpMissionComing,
} from "../../../services/Api/customerService";
import { ToastContainer } from "react-toastify";
import SideBar from "../../../components/Layout/SideBar";
import Header from "../../../components/Layout/Header";
import Footer from "../../../components/Layout/Footer";
import { showToastError } from "../../../services/Toasts/toasts";

const PartnerMonitoring = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { idCustomer, idMission } = useParams();
  const [customerName, setCustomerName] = useState<string>("");
  const [managerName, setManagerName] = useState("");
  const [percent, setPercent] = useState<IData[]>([]);
  const [upComing, setUpComing] = useState<IData[]>([]);
  const [rejected, setRejected] = useState<IData[]>([]);
  const [delivered, setDelivered] = useState<any>([]);
  const [accepted, setAccepted] = useState<any>([]);
  const [lateRequests, setLateRequests] = useState<IData[]>([]);
  const [numberDocRequests, setNumberDocRequests] = useState<number>();

  useEffect(() => {
    Promise.all([
      getPercentageMission(Number(idCustomer), Number(idMission)),
      getMissionDataByCustomer(Number(idCustomer), Number(idMission)),
      getLateRequests(Number(idCustomer), Number(idMission)),
      getUpMissionComing(Number(idCustomer), Number(idMission)),
      getMissionDelivered(Number(idCustomer), Number(idMission)),
      getDocRequests(Number(idCustomer), Number(idMission)),
      getMissionRejected(Number(idCustomer), Number(idMission)),
      getMissionAccepted(Number(idCustomer), Number(idMission)),
    ])
      .then(
        ([
          percentageData,
          missionData,
          lateRequestsData,
          upComingData,
          deliveredData,
          docRequestsData,
          rejectedData,
          acceptedData,
        ]) => {
          const convertedData = convertData(percentageData.percentage);
          setPercent(convertedData);
          const customerNameCut = cutName(missionData?.name);
          setCustomerName(customerNameCut);
          setManagerName(missionData?.manager);
          setLateRequests(lateRequestsData);
          setUpComing(upComingData);
          setDelivered(deliveredData);
          setNumberDocRequests(docRequestsData.length);
          setRejected(rejectedData);
          setAccepted(acceptedData);
        }
      )
      .catch(() => showToastError(t("common.error")));
  }, []);

  return (
    <div className="w-screen h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col justify-start items-start overflow-hidden">
      <Header />
      <div className="w-full h-full flex justify-start items-start">
        <SideBar />
        <ToastContainer position="top-center" />
        <div className="flex flex-col justify-start items-center w-11/12 h-full m-auto">
          {/* Header Section avec design moderne - FIXE */}
          <div className="w-full bg-white rounded-2xl shadow-lg p-6 my-3 border border-gray-100 flex-shrink-0">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-500 rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-700 font-K2D">
                    {t("partner.monitoringTitle")} : <span className="text-blue-500">{customerName}</span>
                  </h1>
                  <p className="text-gray-600 text-md mt-1">
                    {t("partner.missionManager")} : <span className="font-semibold">{managerName}</span>
                  </p>
                </div>
              </div>
              
              <button
                type="button"
                className="px-4 py-2 bg-gray-100 text-gray-700 cursor-pointer font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                onClick={() => navigate(-1)}
              >
                <img src={Return} alt="return" className="w-4 h-4" />
                {t("common.back")}
              </button>
            </div>
          </div>

          {/* Grille de monitoring avec design moderne - AVEC SCROLL */}
          <div className="w-full flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* États des requêtes et Taux de rejet */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 min-h-[400px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                  <div className="flex flex-col h-full">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      {t("partner.requestStates")}
                    </h3>
                    <div className="flex-1 flex items-center justify-center min-h-[200px]">
                      <Camembert data={percent} />
                    </div>
                  </div>
                  
                  <div className="flex flex-col h-full">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      {t("partner.rejectionRate")}
                    </h3>
                    <div className="flex-1 flex items-center justify-center min-h-[200px]">
                      <RejectedRate rejected={rejected} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Requêtes à venir */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 min-h-[400px]">
                <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  {t("partner.upcomingRequests")}
                </h3>
                <div className="flex-1 flex items-center justify-center min-h-[300px]">
                  <LateRequestsDate
                    lateRequests={lateRequests}
                    upComing={upComing}
                    numberDocRequests={numberDocRequests}
                  />
                </div>
              </div>

              {/* Délais d'envoi */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 min-h-[400px]">
                <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  {t("partner.deliveryTimes")}
                </h3>
                <div className="flex-1 flex items-center justify-center min-h-[300px]">
                  <DeliveryTimes
                    delivered={delivered}
                    numberDocRequests={numberDocRequests}
                  />
                </div>
                <div className="mt-4 text-center">
                  <p className="text-gray-600 text-sm">
                    {t("partner.averageDays")} :{" "}
                    <span className="font-semibold text-blue-500 text-base">
                      {mathRoundSup(delivered?.averageDelay)} {t("partner.days")}
                    </span>
                  </p>
                </div>
              </div>

              {/* Délais d'acceptation */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 min-h-[400px]">
                <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  {t("partner.acceptanceTimes")}
                </h3>
                <div className="flex-1 flex items-center justify-center min-h-[300px]">
                  <AcceptedRate
                    accepted={accepted}
                    numberDocRequests={numberDocRequests}
                  />
                </div>
                <div className="mt-4 text-center">
                  <p className="text-gray-600 text-sm">
                    {t("partner.averageDays")} :{" "}
                    <span className="font-semibold text-blue-500 text-base">
                      {mathRoundSup(accepted?.averageDelay)} {t("partner.days")}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PartnerMonitoring;
